import { useMemo, useState } from "react";
import type React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertCircle, CheckCircle2, Clock, KeyRound, Loader2, RefreshCw, Rocket, RotateCw, Server, Settings2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

type Provider = "cpanel" | "plesk" | "ftp_sftp" | "custom";

type HostingConnection = {
  id: string;
  provider: Provider;
  connection_name: string;
  public_config: Record<string, unknown>;
  credentials_metadata: Record<string, { configured?: boolean; preview?: string | null }>;
  validation_status: string;
  last_validated_at: string | null;
  last_validation_error: string | null;
};

type HostingJob = {
  id: string;
  job_type: "publish" | "sync" | "validate";
  status: "pending" | "running" | "success" | "failed";
  created_at: string;
  completed_at: string | null;
  error_message: string | null;
  result: Record<string, unknown>;
};

type HostingLog = {
  id: string;
  level: "info" | "warning" | "error";
  message: string;
  metadata: Record<string, unknown>;
  created_at: string;
};

const providerLabels: Record<Provider, string> = {
  cpanel: "cPanel",
  plesk: "Plesk",
  ftp_sftp: "FTP/SFTP",
  custom: "Custom API",
};

const defaultPorts: Record<Provider, string> = {
  cpanel: "2083",
  plesk: "8443",
  ftp_sftp: "22",
  custom: "",
};

function invokeHosting(body: Record<string, unknown>) {
  return supabase.functions.invoke("website-hosting-manager", { body });
}

export default function HostingConnectionPanel({
  websiteId,
  compact = false,
}: {
  websiteId: string;
  compact?: boolean;
}) {
  const queryClient = useQueryClient();
  const [provider, setProvider] = useState<Provider>("plesk");
  const [form, setForm] = useState({
    host: "",
    api_base_url: "",
    username: "",
    password: "",
    api_token: "",
    port: "",
    domain: "",
    destination_path: "/public_html",
    ssl_option: "auto",
    database_required: false,
    database_name: "",
    dns_required: false,
    dns_zone: "",
    protocol: "sftp",
    sync_enabled: true,
  });

  const summary = useQuery({
    queryKey: ["website-hosting-manager", websiteId],
    queryFn: async () => {
      const { data, error } = await invokeHosting({ action: "list", website_id: websiteId });
      if (error) throw error;
      return data as { connections: HostingConnection[]; jobs: HostingJob[]; website: Record<string, unknown> };
    },
    enabled: !!websiteId,
  });

  const logs = useQuery({
    queryKey: ["website-hosting-logs", websiteId],
    queryFn: async () => {
      const { data, error } = await invokeHosting({ action: "logs", website_id: websiteId });
      if (error) throw error;
      return (data as { logs: HostingLog[] }).logs;
    },
    enabled: !!websiteId,
  });

  const connections = summary.data?.connections ?? [];
  const jobs = summary.data?.jobs ?? [];
  const selectedConnection = connections[0] ?? null;
  const lastJob = jobs[0] ?? null;
  const failedJob = jobs.find((job) => job.status === "failed" && ["publish", "sync"].includes(job.job_type));

  const statusLabel = useMemo(() => {
    if (!selectedConnection) return "Not configured";
    if (selectedConnection.validation_status === "connected") return "Credentials validated";
    if (selectedConnection.validation_status === "failed") return "Validation failed";
    return "Saved, not validated";
  }, [selectedConnection]);

  const actionMutation = useMutation({
    mutationFn: async (body: Record<string, unknown>) => {
      const { data, error } = await invokeHosting({ website_id: websiteId, ...body });
      if (error) throw error;
      return data as Record<string, unknown>;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["website-hosting-manager", websiteId] });
      queryClient.invalidateQueries({ queryKey: ["website-hosting-logs", websiteId] });
      queryClient.invalidateQueries({ queryKey: ["websites"] });
      toast.success(hostingSuccessMessage(String(variables.action)));
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : "Hosting action failed");
      queryClient.invalidateQueries({ queryKey: ["website-hosting-manager", websiteId] });
      queryClient.invalidateQueries({ queryKey: ["website-hosting-logs", websiteId] });
    },
  });

  const saveConnection = () => {
    const publicConfig = {
      host: form.host,
      api_base_url: provider === "custom" ? form.api_base_url : form.host,
      port: form.port || defaultPorts[provider],
      domain: form.domain,
      destination_path: form.destination_path,
      ssl_option: form.ssl_option,
      database_required: form.database_required,
      database_name: form.database_name,
      dns_required: form.dns_required,
      dns_zone: form.dns_zone,
      protocol: provider === "ftp_sftp" ? form.protocol : undefined,
    };
    const credentials = provider === "cpanel"
      ? { username: form.username, api_token: form.api_token }
      : provider === "custom"
        ? { api_token: form.api_token }
        : { username: form.username, password: form.password };

    actionMutation.mutate({
      action: "save_connection",
      provider,
      sync_enabled: form.sync_enabled,
      sync_configuration: {
        include: ["website_type", "content", "design_theme", "pages", "media", "settings", "login_configuration_metadata"],
      },
      public_config: publicConfig,
      credentials,
    });
  };

  const runAction = (action: "validate" | "publish" | "sync") => {
    if (!selectedConnection) {
      toast.error("Configure external hosting before this action");
      return;
    }
    actionMutation.mutate({ action, connection_id: selectedConnection.id });
  };

  const retry = () => {
    if (!selectedConnection || !failedJob) return;
    actionMutation.mutate({ action: "retry", connection_id: selectedConnection.id, job_id: failedJob.id });
  };

  return (
    <div className={cn("rounded-lg border border-border bg-card", compact ? "p-3" : "p-4")}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-accent">
            <Server className="h-4 w-4 text-muted-foreground" />
          </div>
          <div>
            <p className="text-sm font-semibold text-foreground">External hosting and publishing</p>
            <p className="text-xs text-muted-foreground">{statusLabel}</p>
          </div>
        </div>
        <StatusPill status={selectedConnection?.validation_status || "not_configured"} />
      </div>

      <div className="mt-4 grid gap-3 lg:grid-cols-[1.1fr_1fr]">
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
            {(Object.keys(providerLabels) as Provider[]).map((item) => (
              <button
                key={item}
                type="button"
                onClick={() => {
                  setProvider(item);
                  setForm((prev) => ({ ...prev, port: defaultPorts[item] }));
                }}
                className={cn(
                  "rounded-md border px-2.5 py-2 text-xs font-medium transition-colors",
                  provider === item ? "border-primary/40 bg-primary/10 text-primary" : "border-border text-muted-foreground hover:text-foreground",
                )}
              >
                {providerLabels[item]}
              </button>
            ))}
          </div>

          <div className="grid gap-2 md:grid-cols-2">
            <Input label={provider === "custom" ? "API base URL" : "Host URL/IP"} value={provider === "custom" ? form.api_base_url : form.host} onChange={(value) => setForm((prev) => provider === "custom" ? { ...prev, api_base_url: value } : { ...prev, host: value })} placeholder={provider === "custom" ? "https://api.host.example" : "https://panel.example.com"} />
            <Input label="Domain" value={form.domain} onChange={(value) => setForm((prev) => ({ ...prev, domain: value }))} placeholder="example.com" />
            {provider !== "custom" && <Input label="Username" value={form.username} onChange={(value) => setForm((prev) => ({ ...prev, username: value }))} placeholder="admin" />}
            {provider === "cpanel" || provider === "custom" ? (
              <Input label="API token" value={form.api_token} onChange={(value) => setForm((prev) => ({ ...prev, api_token: value }))} placeholder="Stored encrypted" secret />
            ) : (
              <Input label="Password" value={form.password} onChange={(value) => setForm((prev) => ({ ...prev, password: value }))} placeholder="Stored encrypted" secret />
            )}
            <Input label="Port" value={form.port || defaultPorts[provider]} onChange={(value) => setForm((prev) => ({ ...prev, port: value }))} placeholder="22" />
            <Input label="Destination path" value={form.destination_path} onChange={(value) => setForm((prev) => ({ ...prev, destination_path: value }))} placeholder="/public_html" />
            <Input label="SSL option" value={form.ssl_option} onChange={(value) => setForm((prev) => ({ ...prev, ssl_option: value }))} placeholder="auto" />
            {provider === "ftp_sftp" && <Input label="Protocol" value={form.protocol} onChange={(value) => setForm((prev) => ({ ...prev, protocol: value }))} placeholder="sftp" />}
          </div>

          <div className="grid gap-2 md:grid-cols-2">
            <Toggle label="Database details required" checked={form.database_required} onChange={(value) => setForm((prev) => ({ ...prev, database_required: value }))} />
            <Toggle label="DNS details required" checked={form.dns_required} onChange={(value) => setForm((prev) => ({ ...prev, dns_required: value }))} />
          </div>
          {(form.database_required || form.dns_required) && (
            <div className="grid gap-2 md:grid-cols-2">
              {form.database_required && <Input label="Database name/details" value={form.database_name} onChange={(value) => setForm((prev) => ({ ...prev, database_name: value }))} placeholder="db name or note" />}
              {form.dns_required && <Input label="DNS zone/details" value={form.dns_zone} onChange={(value) => setForm((prev) => ({ ...prev, dns_zone: value }))} placeholder="DNS zone or note" />}
            </div>
          )}
          <Toggle label="Enable synchronization for this host" checked={form.sync_enabled} onChange={(value) => setForm((prev) => ({ ...prev, sync_enabled: value }))} />

          <div className="flex flex-wrap gap-2">
            <Button icon={KeyRound} label="Save credentials" onClick={saveConnection} loading={actionMutation.isPending} />
            <Button icon={CheckCircle2} label="Validate" onClick={() => runAction("validate")} loading={actionMutation.isPending} variant="secondary" />
            <Button icon={Rocket} label="Publish" onClick={() => runAction("publish")} loading={actionMutation.isPending} variant="secondary" />
            <Button icon={RotateCw} label="Synchronize" onClick={() => runAction("sync")} loading={actionMutation.isPending} variant="secondary" />
            {failedJob && <Button icon={RefreshCw} label="Retry failed" onClick={retry} loading={actionMutation.isPending} variant="danger" />}
          </div>
        </div>

        <div className="space-y-3">
          <div className="rounded-md border border-border bg-background/50 p-3">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              <Settings2 className="h-3.5 w-3.5" />
              Current configuration
            </div>
            {!selectedConnection ? (
              <p className="mt-3 text-xs text-muted-foreground">No external host configured. Delapresse will manage this website internally until credentials are saved.</p>
            ) : (
              <div className="mt-3 space-y-2 text-xs">
                <Info label="Provider" value={providerLabels[selectedConnection.provider]} />
                <Info label="Host" value={String(selectedConnection.public_config.host || selectedConnection.public_config.api_base_url || "-")} />
                <Info label="Domain" value={String(selectedConnection.public_config.domain || "-")} />
                <Info label="Destination" value={String(selectedConnection.public_config.destination_path || "-")} />
                <Info label="Secrets" value={Object.keys(selectedConnection.credentials_metadata || {}).join(", ") || "Configured"} />
              </div>
            )}
          </div>

          <div className="rounded-md border border-border bg-background/50 p-3">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              <Clock className="h-3.5 w-3.5" />
              Publish/sync status
            </div>
            {summary.isLoading ? (
              <Loader2 className="mt-4 h-4 w-4 animate-spin text-muted-foreground" />
            ) : jobs.length === 0 ? (
              <p className="mt-3 text-xs text-muted-foreground">No publish or sync execution yet.</p>
            ) : (
              <div className="mt-3 space-y-2">
                {jobs.slice(0, compact ? 3 : 6).map((job) => (
                  <div key={job.id} className="rounded border border-border px-2.5 py-2 text-xs">
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-medium capitalize text-foreground">{job.job_type}</span>
                      <JobStatus status={job.status} />
                    </div>
                    <p className="mt-1 text-muted-foreground">{new Date(job.completed_at || job.created_at).toLocaleString()}</p>
                    {job.error_message && <p className="mt-1 text-destructive">{job.error_message}</p>}
                  </div>
                ))}
              </div>
            )}
          </div>

          {!compact && (
            <div className="rounded-md border border-border bg-background/50 p-3">
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                <AlertCircle className="h-3.5 w-3.5" />
                Logs
              </div>
              {(logs.data ?? []).length === 0 ? (
                <p className="mt-3 text-xs text-muted-foreground">No logs yet.</p>
              ) : (
                <div className="mt-3 max-h-56 space-y-2 overflow-auto">
                  {(logs.data ?? []).slice(0, 20).map((item) => (
                    <div key={item.id} className="text-xs">
                      <span className={cn("font-medium", item.level === "error" && "text-destructive", item.level === "warning" && "text-amber-500")}>{item.level}</span>
                      <span className="text-muted-foreground"> · {new Date(item.created_at).toLocaleString()}</span>
                      <p className="text-foreground">{item.message}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {lastJob?.error_message && (
        <p className="mt-3 text-xs text-destructive">{lastJob.error_message}</p>
      )}
    </div>
  );
}

function hostingSuccessMessage(action: string) {
  if (action === "save_connection") return "Hosting credentials saved";
  if (action === "validate") return "Hosting validation completed";
  if (action === "publish") return "Publish execution completed";
  if (action === "sync") return "Synchronization completed";
  if (action === "retry") return "Retry execution completed";
  return "Hosting action completed";
}

function Input({ label, value, onChange, placeholder, secret = false }: { label: string; value: string; onChange: (value: string) => void; placeholder?: string; secret?: boolean }) {
  return (
    <label className="space-y-1">
      <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">{label}</span>
      <input
        type={secret ? "password" : "text"}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        className="w-full rounded-md border border-border bg-background px-2.5 py-2 text-xs text-foreground outline-none focus:ring-1 focus:ring-ring"
      />
    </label>
  );
}

function Toggle({ label, checked, onChange }: { label: string; checked: boolean; onChange: (checked: boolean) => void }) {
  return (
    <label className="flex items-center justify-between gap-3 rounded-md border border-border bg-background px-2.5 py-2 text-xs text-foreground">
      <span>{label}</span>
      <input type="checkbox" checked={checked} onChange={(event) => onChange(event.target.checked)} className="rounded border-border" />
    </label>
  );
}

function Button({ icon: Icon, label, onClick, loading, variant = "primary" }: { icon: React.ElementType; label: string; onClick: () => void; loading?: boolean; variant?: "primary" | "secondary" | "danger" }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={loading}
      className={cn(
        "inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition-colors disabled:opacity-50",
        variant === "primary" && "bg-primary text-primary-foreground hover:bg-primary/90",
        variant === "secondary" && "border border-border text-foreground hover:bg-accent",
        variant === "danger" && "border border-destructive/30 bg-destructive/10 text-destructive hover:bg-destructive/15",
      )}
    >
      {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Icon className="h-3.5 w-3.5" />}
      {label}
    </button>
  );
}

function Info({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-3">
      <span className="text-muted-foreground">{label}</span>
      <span className="text-right font-medium text-foreground">{value}</span>
    </div>
  );
}

function StatusPill({ status }: { status: string }) {
  const connected = status === "connected";
  const failed = status === "failed";
  return (
    <span className={cn(
      "inline-flex items-center gap-1 rounded-full px-2 py-1 text-[10px] font-semibold uppercase tracking-wide",
      connected && "bg-emerald-500/10 text-emerald-500",
      failed && "bg-destructive/10 text-destructive",
      !connected && !failed && "bg-muted text-muted-foreground",
    )}>
      {connected ? <CheckCircle2 className="h-3 w-3" /> : failed ? <AlertCircle className="h-3 w-3" /> : <Clock className="h-3 w-3" />}
      {status.replace(/_/g, " ")}
    </span>
  );
}

function JobStatus({ status }: { status: HostingJob["status"] }) {
  return (
    <span className={cn(
      "rounded-full px-2 py-0.5 text-[10px] font-medium capitalize",
      status === "success" && "bg-emerald-500/10 text-emerald-500",
      status === "failed" && "bg-destructive/10 text-destructive",
      status === "running" && "bg-primary/10 text-primary",
      status === "pending" && "bg-muted text-muted-foreground",
    )}>
      {status}
    </span>
  );
}
